import { resolvePeerProductUrl } from '@ambr/core/src/helpers/productEnvironment'
import { ConfigProvider } from '@ambr/core/src/config/ConfigProvider'

/**
 * The shared cookie domain, or `undefined` where a `Domain` attribute cannot apply.
 *
 * Browsers reject `Domain` on a single-label host such as `localhost` without
 * reporting an error, so the attribute has to be absent rather than empty. A
 * leading dot is stripped: it is the same scope per RFC 6265.
 */
export const resolveCookieDomain = (raw: string | undefined): string | undefined => {
  const domain = raw?.trim().replace(/^\./, '')
  if (!domain || !domain.includes('.')) {
    return undefined
  }
  return domain
}

/** The `product` this app identifies itself as in Cognito client metadata. */
const PRODUCT = 'design'

/**
 * Client metadata for the Cognito calls this app makes, which Cognito forwards
 * to the custom email sender.
 *
 * The sender emails a verification link for this product and a code for the
 * others, so the metadata is sent only where the app is set up to accept a
 * link: an environment with `VITE_EMAIL_VERIFICATION_LINKS` off asks for a code
 * on the confirmation screen and must be emailed one to match.
 */
export const cognitoClientMetadata = (
  emailVerificationLinks: boolean
): Record<string, string> | undefined => (emailVerificationLinks ? { product: PRODUCT } : undefined)

/** Registers environment endpoints and cookie settings before auth initializes. */
export const configure = (
  location: Pick<Location, 'hostname' | 'protocol'> = window.location
): void => {
  const cookieDomain = resolveCookieDomain(import.meta.env.VITE_COGNITO_COOKIE_STORAGE_DOMAIN)
  const emailVerificationLinks = import.meta.env.VITE_EMAIL_VERIFICATION_LINKS === 'true'
  ConfigProvider.register({
    ambrApiUrl: import.meta.env.VITE_API_NEXT_ENDPOINT,
    passwordResetUrl: import.meta.env.VITE_COGNITO_PASSWORD_RESET_URL,
    signUpUrl: import.meta.env.VITE_COGNITO_SIGN_UP_URL,
    mlServiceUrl: import.meta.env.VITE_ML_ENDPOINT,
    emailVerificationLinks,
    motifReviewUrl: resolvePeerProductUrl(import.meta.env.VITE_MOTIF_REVIEW_URL, location.hostname),
    cognito: {
      region: import.meta.env.VITE_COGNITO_REGION,
      userPoolId: import.meta.env.VITE_COGNITO_USER_POOL_ID,
      userPoolWebClientId: import.meta.env.VITE_COGNITO_USER_POOL_WEB_CLIENT_ID,
      mandatorySignIn: true,
      clientMetadata: cognitoClientMetadata(emailVerificationLinks),
      cookieStorage: {
        ...(cookieDomain ? { domain: cookieDomain } : {}),
        path: '/',
        // Preserve the existing persistence policy; Cognito controls token validity.
        expires: 365,
        // Safari rejects a Secure cookie on an insecure origin, which breaks local sign-in.
        secure: location.protocol === 'https:',
        // Both products are same-site once they share a registrable domain.
        sameSite: 'lax' as const,
      },
    },
  })
}
